import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { evaluateRules, type PracticeConfiguration } from './rules';
import { evaluatePrenatalRules } from './prenatalRules';
import { addDays } from './calculations';
import type { ClinicalEvent } from './types';

/** Isolated synthetic evaluation: never publishes configuration or patient observations. */
export function RulePreview({ configuration }: { configuration: PracticeConfiguration }) {
  const [values, setValues] = useState({
    ga: '196',
    systolic: '118',
    diastolic: '72',
    fh: '28',
    fht: '140',
    weight: '75000',
  });
  const labels: Record<keyof typeof values, string> = {
    ga: 'Gestational age (days)',
    systolic: 'Systolic BP',
    diastolic: 'Diastolic BP',
    fh: 'Fundal height (cm)',
    fht: 'Fetal heart rate (bpm)',
    weight: 'Weight (grams)',
  };
  const valid =
    Object.values(values).every((value) => value.trim() && Number.isFinite(Number(value))) &&
    Number.isInteger(Number(values.ga)) &&
    Number(values.ga) >= 0 &&
    Number(values.ga) <= 350;
  const date = '2026-09-22';
  const event = (type: string, payload: Record<string, unknown>, index: number): ClinicalEvent => ({
    id: `preview-${index}`,
    root_id: `preview-${index}`,
    episode_id: 'synthetic',
    encounter_id: 'synthetic',
    subject_id: type === 'PRENATAL.FHT' ? 'fetus' : 'mother',
    sequence: index,
    kind: 'CREATE',
    event_type: type,
    schema_version: 1,
    payload,
    status: 'ACTIVE',
    occurred_at: `${date}T12:00:00Z`,
    recorded_at: `${date}T12:00:00Z`,
    server_received_at: `${date}T12:00:00Z`,
    author_id: 'synthetic',
    author_initials: 'TEST',
    author_role: 'simulation',
    source: 'MANUAL',
    reason: null,
    supersedes_id: null,
  });
  const events = valid
    ? [
        event('VITALS.BP', { systolic: Number(values.systolic), diastolic: Number(values.diastolic) }, 1),
        event('PRENATAL.FUNDAL_HEIGHT', { cm: Number(values.fh) }, 2),
        event('PRENATAL.FHT', { bpm: Number(values.fht) }, 3),
        event('VITALS.WEIGHT', { grams: Number(values.weight) }, 4),
      ]
    : [];
  // Simulate review only in this isolated evaluator. The publication checkbox is unchanged.
  const simulated = { ...configuration, clinically_reviewed: true };
  const flags = valid
    ? [
        ...evaluateRules(events, simulated),
        ...evaluatePrenatalRules(events, simulated, {
          edd: addDays(date, 280 - Number(values.ga)),
          onDate: date,
          timeZone: 'UTC',
          prePregnancyWeightGrams: 65000,
          heightCm: 165,
          fetusCount: 1,
        }),
      ]
    : [];
  return (
    <details className="rounded-lg border p-4">
      <summary className="min-h-11 cursor-pointer font-medium">Preview rules on a synthetic chart</summary>
      <p className="my-3 text-sm text-muted-foreground">
        Simulation only; no patient data is saved. Uses the proposed enabled settings as if reviewed, a singleton
        pregnancy, baseline 65 kg and height 165 cm. Publication still requires your review. Unrecorded laboratory
        status remains unknown.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(Object.keys(values) as (keyof typeof values)[]).map((key) => (
          <Field key={key}>
            <FieldLabel htmlFor={`preview-${key}`}>{labels[key]}</FieldLabel>
            <Input
              id={`preview-${key}`}
              type="number"
              inputMode="decimal"
              value={values[key]}
              onChange={(e) => setValues({ ...values, [key]: e.target.value })}
            />
          </Field>
        ))}
      </div>
      <div className="mt-4" aria-live="polite">
        {!valid ? (
          <p>Enter valid numeric values; gestational age must be an integer from 0 to 350 days.</p>
        ) : flags.length ? (
          <ul className="list-disc pl-5">
            {flags.map((flag) => (
              <li key={`${flag.ruleId}:${flag.eventId}`}>
                <strong>
                  {flag.severity} · {flag.ruleId}:
                </strong>{' '}
                {flag.message}
              </li>
            ))}
          </ul>
        ) : (
          <p>
            No enabled rule matches this sample. This does not establish that the chart or configuration is clinically
            normal.
          </p>
        )}
      </div>
    </details>
  );
}
